import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiLoading } from '@mdi/js'
import { $api } from '../api/queryClient'
import { useToast } from '../hooks/use-toast'
import { AppIcon } from './ui/icon'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'
import { Label } from './ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'

const REASON_MAX = 500

/**
 * « Non applicable » on a checklist row (spec 2026-10-11-17): a required
 * reason, then PUT /assets/{uid}/contract-checklist/waivers/{typeUid}. The
 * caller passes the type to waive, null to close.
 */
export function ContractWaiverDialog({
  org,
  assetUid,
  assetName,
  contractType,
  onOpenChange,
  onDone,
}: {
  org: string
  assetUid: string
  assetName: string
  contractType: { uid: string; name: string } | null
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  const { t } = useTranslation('contracts')
  const { toast } = useToast()
  const [reason, setReason] = useState('')

  const waive = $api.useMutation('put', '/api/v1/organizations/{orgCode}/assets/{assetUid}/contract-checklist/waivers/{contractTypeUid}', {
    onSuccess: () => {
      setReason('')
      onDone()
      onOpenChange(false)
    },
    onError: () => {
      toast({ title: t('detail.error_title'), description: t('checklist.waive_error'), variant: 'destructive' })
    },
  })

  const confirm = () => {
    if (!contractType || reason.trim() === '') return
    waive.mutate({
      params: { path: { orgCode: org, assetUid, contractTypeUid: contractType.uid } },
      body: { reason: reason.trim() },
    })
  }

  return (
    <Dialog open={!!contractType} onOpenChange={onOpenChange}>
      <DialogContent data-testid="contract-checklist-waive-dialog">
        <DialogHeader>
          <DialogTitle>{t('checklist.waive_title', { asset: assetName })}</DialogTitle>
          <DialogDescription>{t('checklist.waive_description', { type: contractType?.name ?? '' })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="contract-checklist-waive-reason">{t('checklist.waive_reason_label')}</Label>
          <Textarea
            id="contract-checklist-waive-reason"
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('checklist.waive_reason_placeholder')}
            data-testid="contract-checklist-waive-reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contract-checklist-waive-cancel">
            {t('checklist.waive_cancel')}
          </Button>
          <Button onClick={confirm} disabled={waive.isPending || reason.trim() === ''} data-testid="contract-checklist-waive-confirm">
            {waive.isPending && <AppIcon path={mdiLoading} size="xs" className="mr-1 motion-safe:animate-spin" />}
            {t('checklist.waive_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
